<template>
  <slot
    v-if="connection !== undefined && connectionData !== undefined"
    name="default"
    :connection-disabled="disabled"
    :connection="connection"
    :connection-data="connectionData"
  >
    <svg
      :id="`connection-${connectionId}`"
      class="connection"
    >
      <path
        ref="pathElement"
        d=""
      />
    </svg>
  </slot>
</template>

<script setup lang="ts" generic="SCHEMA extends INodeSchemaRegistry = INodeSchemaRegistry">
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  useTemplateRef, watch,
} from 'vue';

import {
  GraphEditor,
} from '../core';
import {
  IConnectionPosition,
} from '../core/connection-manager';
import {
  IGraphEditorEvents,
  INodeSchemaRegistry,
  TEntityIdentifier,
} from '../types';

import {
  applyConnectionData,
} from './connection-utils';
import {
  ISlotPropsConnection,
  IUserInterfaceAdapterProps,
} from './types';

const props = defineProps<IUserInterfaceAdapterProps & {
  graphEditor: GraphEditor<SCHEMA>,
  connectionId: TEntityIdentifier,
  canvasElementId?: string,
}>();

defineSlots<{
  default(props: ISlotPropsConnection): void,
}>();

const pathElement = useTemplateRef('pathElement');
const connection = computed(() => props.graphEditor.getConnection(props.connectionId));
const connectionData = ref<IConnectionPosition | undefined>();

function updateConnectionData() {
  // Get connection position data from the graph editor
  const viewBoxOffset = 10;
  connectionData.value = props.graphEditor.buildConnection(props.connectionId, viewBoxOffset);

  if (!pathElement.value) {
    return;
  }
  applyConnectionData(pathElement.value, connectionData.value);
}

let requestAnimationFrameId: number | undefined;
function requestConnectionUpdate() {
  if (requestAnimationFrameId !== undefined) {
    return;
  }
  requestAnimationFrameId = requestAnimationFrame(() => {
    updateConnectionData();
    requestAnimationFrameId = undefined;
  });
}

function onNodeMoved(event: IGraphEditorEvents['node:moved']) {
  if (!connection.value || (event.id !== connection.value.fromNodeId && event.id !== connection.value.toNodeId)) {
    return;
  }
  requestConnectionUpdate();
}

function onNodeResized(event: IGraphEditorEvents['node:resized']) {
  if (!connection.value || (event.id !== connection.value.fromNodeId && event.id !== connection.value.toNodeId)) {
    return;
  }
  requestConnectionUpdate();
}

function onCanvasChanged() {
  // Update connection when canvas zoom/pan changes
  requestConnectionUpdate();
}

onMounted(() => {
  props.graphEditor.addEventListener('node:moved', onNodeMoved);
  props.graphEditor.addEventListener('node:resized', onNodeResized);
  props.graphEditor.addEventListener('canvas:changed', onCanvasChanged);
  props.graphEditor.addEventListener('allNodesMounted', updateConnectionData);

  // Initial update
  updateConnectionData();
});

onBeforeUnmount(() => {
  props.graphEditor.removeEventListener('node:moved', onNodeMoved);
  props.graphEditor.removeEventListener('node:resized', onNodeResized);
  props.graphEditor.removeEventListener('canvas:changed', onCanvasChanged);
  props.graphEditor.removeEventListener('allNodesMounted', updateConnectionData);
});

watch(pathElement, updateConnectionData);
</script>

<style scoped lang="scss">
@use './styles.scss';

.connection {
  z-index: styles.$z-level-connection;
  position: absolute;
  overflow: visible !important;
  pointer-events: none;

  path {
    fill: none;
    pointer-events: auto;
    stroke: #000;
    stroke-width: 2;
  }
}
</style>
